import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import {
  createPackage,
  updatePackage,
  fetchAllPackages,
} from "../api/package.api";
import {
  CreatePackagePayload,
  UpdatePackagePayload,
} from "../api/models/package.models";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface UsePackagesProps {
  companyId?: number;
}

const usePackages = ({ companyId }: UsePackagesProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const [bearerToken, toast] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken, state.toastRef])
  );

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["packages", companyId],
    queryFn: () => fetchAllPackages(bearerToken, companyId),
    enabled: !!companyId,
  });

  // Create new simple package
  const createNewPackage = async (payload: CreatePackagePayload) => {
    const result = await createPackageMutation.mutateAsync(payload);
    return result;
  };

  const createPackageMutation = useMutation({
    mutationFn: (payload: CreatePackagePayload) =>
      createPackage(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to create new simple package:", error);
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("text-package-created"),
        life: 3000,
      });
      queryClient.invalidateQueries({
        queryKey: ["packages"],
      });
    },
  });

  // Update existing simple package
  const updateExistingPackage = (payload: UpdatePackagePayload) => {
    const result = updatePackageMutation.mutateAsync(payload);
    return result;
  };

  const updatePackageMutation = useMutation({
    mutationFn: (payload: UpdatePackagePayload) =>
      updatePackage(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to update simple package:", error);
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("text-package-updated"),
        life: 3000,
      });
      queryClient.invalidateQueries({
        queryKey: ["packages"],
      });
    },
  });

  const isUpdatingPackage = updatePackageMutation.isPending;
  const isCreatingPackage = createPackageMutation.isPending;

  return {
    packages: data,
    error,
    isLoading,
    createNewPackage,
    updateExistingPackage,
    isUpdatingPackage,
    isCreatingPackage,
  };
};

export default usePackages;
